import { Button, Stack, TextField } from '@mui/material';
import React from 'react';
import { handleCommandKeyDown, handlePayloadKeyDown } from '../../keyboardShortcuts';
import UserFetch, { UserPost } from '../../userFetch';
import CommandState from './commandState';
import Command from '../../models/command';
import { useContext } from 'use-context-selector';
import { DevicesContext } from './deviceContext';

interface DeviceCommandProps {
  label?: string | string[],
  serial_number: string,
  direction?: 'row' | 'column',
  ongoingCommand?: Command,
  onCommandChange?: () => void,
  commandInputRef?: React.Ref<HTMLInputElement>
}

const DeviceCommand = function (props: DeviceCommandProps) {
  const [command, setCommand] = React.useState('');
  const [payload, setPayload] = React.useState('');
  const commandBusy = !!props.ongoingCommand?.requested && !props.ongoingCommand?.completed;
  const [deleteButtonColor, setDeleteButtonColor] = React.useState<'success' | 'error' | 'info'>('info');
  const devicesDispatch = useContext(DevicesContext).dispatch;

  const handleSend = React.useCallback(() => {
    UserPost(`/api/devices/${props.serial_number}/command`, {
      operation: command,
      payload: payload
    }).then(() => {

      devicesDispatch({
        type: 'SENDING_COMMAND',
        payload: [{
          serial_number: props.serial_number,
          command,
        }]
      });

      props.onCommandChange?.();
    });
    setCommand('');
    setPayload('');
  }, [command, payload, props.serial_number]);

  const handleDelete = React.useCallback(() => {
    UserFetch(`/api/devices/${props.serial_number}/command`, {
      method: 'DELETE'
    })
      .then((response: Response) => {
        setDeleteButtonColor(response.ok ? 'success' : 'error');

        devicesDispatch({
          type: 'COMMAND_SENT',
          payload: [{
            serial_number: props.serial_number,
          }]
        });

      })
      .catch(() => {
        setDeleteButtonColor('error');
      })
      .then(() => {
        setTimeout(() => {
          setDeleteButtonColor('info');
        }, 2000);
      }).then(() => {
        props.onCommandChange?.();
      });
  }, [props.serial_number]);

  return (
    <Stack spacing={2} sx={{ width: '20%', minWidth: 0, overflow: 'hidden' }}>
      <Stack direction={props.direction || 'row'} spacing={1}>
        <TextField
          inputRef={props.commandInputRef}
          slotProps={{
            htmlInput: { 'data': `device-command-input-${props.direction || 'row'}` },
          }}
          margin="dense"
          size="small"
          label="Command"
          value={command}
          disabled={commandBusy}
          onChange={(e) => setCommand(e.target.value)}
          onKeyDown={(e) => handleCommandKeyDown(e, command, payload, handleSend)}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#f75961',
              },
              '&:hover fieldset': {
                borderColor: '#f75961',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#f75961',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#f75961',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#f75961',
            },
            '& .MuiInputBase-input': {
              color: '#E5E5E5',
            },
          }}
        />
        <TextField
          multiline
          maxRows={4}
          margin="dense"
          size="small"
          label="Payload"
          value={payload}
          disabled={commandBusy}
          onChange={(e) => setPayload(e.target.value)}
          onKeyDown={(e) => handlePayloadKeyDown(e, command, payload, handleSend)}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#f75961',
              },
              '&:hover fieldset': {
                borderColor: '#f75961',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#f75961',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#f75961',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#f75961',
            },
            '& .MuiInputBase-input': {
              color: '#E5E5E5',
            },
          }}
        />
        <Button
          variant="outlined"
          disabled={commandBusy}
          onClick={handleSend}
          sx={{
            color: '#f75961',
            borderColor: '#f75961',
            '&:hover': {
              borderColor: '#f75961',
              backgroundColor: 'rgba(247, 89, 97, 0.1)'
            }
          }}>Send</Button>
        <Button
          variant="outlined"
          color={deleteButtonColor}
          onClick={handleDelete}
          sx={{
            color: deleteButtonColor === 'info' ? '#f75961' : undefined,
            borderColor: deleteButtonColor === 'info' ? '#f75961' : undefined,
            '&:hover': deleteButtonColor === 'info' ? {
              borderColor: '#f75961',
              backgroundColor: 'rgba(247, 89, 97, 0.1)'
            } : undefined
          }}>Cancel</Button>
      </Stack >
      <CommandState command={props.ongoingCommand || {}} />
    </Stack>
  );
}

export default React.memo(DeviceCommand);